import { useState } from "react";

const SESSION_SEED_KEY = "as-welcome-banner-seed";
const VIEW_W = 1200;
const VIEW_H = 260;

type Ribbon = {
  top: string;
  soft: boolean;
};

type BannerScene = {
  ribbons: Ribbon[];
  strokes: string[];
  orbs: Array<{ cx: number; cy: number; r: number; opacity: number }>;
  nodes: Array<{ cx: number; cy: number; r: number; opacity: number }>;
};

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function sessionSeed(): number {
  try {
    const existing = sessionStorage.getItem(SESSION_SEED_KEY);
    if (existing != null && existing !== "") {
      const n = Number(existing);
      if (Number.isFinite(n)) return n >>> 0;
    }
    const next = (Math.random() * 0xffffffff) >>> 0;
    sessionStorage.setItem(SESSION_SEED_KEY, String(next));
    return next;
  } catch {
    return (Math.random() * 0xffffffff) >>> 0;
  }
}

/** Sample a smooth wave y(x) from right → left with seeded phase/amp. */
function waveY(
  x: number,
  baseY: number,
  amp: number,
  freq: number,
  phase: number,
): number {
  return (
    baseY +
    Math.sin((x / VIEW_W) * Math.PI * freq + phase) * amp +
    Math.sin((x / VIEW_W) * Math.PI * (freq * 1.7) + phase * 1.3) * amp * 0.35
  );
}

function cubicThrough(pts: Array<{ x: number; y: number }>): string {
  if (pts.length === 0) return "";
  let d = `M${pts[0]!.x.toFixed(1)} ${pts[0]!.y.toFixed(1)}`;
  for (let i = 1; i < pts.length; i++) {
    const p0 = pts[i - 1]!;
    const p1 = pts[i]!;
    const cx = ((p0.x + p1.x) / 2).toFixed(1);
    d += ` C${cx} ${p0.y.toFixed(1)} ${cx} ${p1.y.toFixed(1)} ${p1.x.toFixed(1)} ${p1.y.toFixed(1)}`;
  }
  return d;
}

function buildRibbonPath(
  rand: () => number,
  baseY: number,
  thickness: number,
  amp: number,
  freq: number,
): string {
  const phase = rand() * Math.PI * 2;
  const ampJitter = amp * (0.85 + rand() * 0.3);
  const steps = 8;
  const topPts: Array<{ x: number; y: number }> = [];
  const botPts: Array<{ x: number; y: number }> = [];
  for (let i = 0; i <= steps; i++) {
    const x = VIEW_W - (i / steps) * VIEW_W;
    topPts.push({ x, y: waveY(x, baseY, ampJitter, freq, phase) });
    botPts.push({
      x,
      y: waveY(x, baseY + thickness, ampJitter * 0.92, freq, phase + 0.15),
    });
  }
  // Right → left along top, left → right along bottom, close at right edge
  return `${cubicThrough(topPts)} ${cubicThrough([...botPts].reverse()).replace(/^M/, "L")} Z`;
}

function buildStroke(
  rand: () => number,
  baseY: number,
  amp: number,
  freq: number,
): string {
  const phase = rand() * Math.PI * 2;
  const reach = 0.45 + rand() * 0.15;
  const steps = 6;
  const pts: Array<{ x: number; y: number }> = [];
  for (let i = 0; i <= steps; i++) {
    const x = VIEW_W - (i / steps) * (VIEW_W * reach);
    pts.push({ x, y: waveY(x, baseY, amp, freq, phase) });
  }
  return cubicThrough(pts);
}

function generateScene(seed: number): BannerScene {
  const rand = mulberry32(seed);
  const ribbonCount = 3 + Math.floor(rand() * 2); // 3–4
  const ribbons: Ribbon[] = [];
  let y = 24 + rand() * 20;
  for (let i = 0; i < ribbonCount; i++) {
    const thickness = 38 + rand() * 22;
    const amp = 10 + rand() * 18;
    const freq = 1.2 + rand() * 1.8;
    ribbons.push({
      top: buildRibbonPath(rand, y, thickness, amp, freq),
      soft: i % 2 === 0,
    });
    y += thickness * (0.55 + rand() * 0.25);
  }

  const strokes: string[] = [];
  for (let i = 0; i < 4; i++) {
    strokes.push(
      buildStroke(
        rand,
        50 + i * 48 + rand() * 12,
        8 + rand() * 10,
        1.4 + rand(),
      ),
    );
  }

  const orbs = Array.from({ length: 3 }, (_, i) => ({
    cx: VIEW_W - rand() * 80 - i * 40,
    cy: 20 + rand() * 200,
    r: 90 + rand() * 70,
    opacity: 0.35 + rand() * 0.3,
  }));

  const nodes = Array.from({ length: 7 }, () => ({
    cx: VIEW_W - 20 - rand() * 320,
    cy: 40 + rand() * 180,
    r: 2.5 + rand() * 2.5,
    opacity: 0.12 + rand() * 0.28,
  }));

  return { ribbons, strokes, orbs, nodes };
}

export function WelcomeBannerArt() {
  const [scene] = useState(() => generateScene(sessionSeed()));

  return (
    <div className="rs-welcome-art" aria-hidden="true">
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="xMaxYMid slice"
      >
        <defs>
          <linearGradient id="rs-welcome-sky" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="var(--rs-bg)" />
            <stop offset="0.5" stopColor="var(--rs-bg-muted)" />
            <stop
              offset="1"
              stopColor="var(--rs-accent-soft)"
              stopOpacity="0.55"
            />
          </linearGradient>
          <linearGradient id="rs-welcome-ribbon" x1="1" y1="0" x2="0" y2="0">
            <stop offset="0" stopColor="var(--rs-accent)" stopOpacity="0.4" />
            <stop
              offset="0.25"
              stopColor="var(--rs-accent)"
              stopOpacity="0.35"
            />
            <stop
              offset="0.55"
              stopColor="var(--rs-accent-soft)"
              stopOpacity="0.55"
            />
            <stop
              offset="1"
              stopColor="var(--rs-accent-soft)"
              stopOpacity="0"
            />
          </linearGradient>
          <linearGradient
            id="rs-welcome-ribbon-soft"
            x1="1"
            y1="0"
            x2="0"
            y2="0"
          >
            <stop
              offset="0"
              stopColor="var(--rs-accent-hover)"
              stopOpacity="0.32"
            />
            <stop
              offset="0.4"
              stopColor="var(--rs-accent-soft)"
              stopOpacity="0.4"
            />
            <stop
              offset="1"
              stopColor="var(--rs-accent-soft)"
              stopOpacity="0"
            />
          </linearGradient>
        </defs>

        <rect width={VIEW_W} height={VIEW_H} fill="url(#rs-welcome-sky)" />

        {scene.orbs.map((orb, i) => (
          <circle
            key={`orb-${i}`}
            cx={orb.cx}
            cy={orb.cy}
            r={orb.r}
            fill="var(--rs-accent-soft)"
            opacity={orb.opacity}
          />
        ))}

        {scene.ribbons.map((ribbon, i) => (
          <path
            key={`ribbon-${i}`}
            d={ribbon.top}
            fill={
              ribbon.soft
                ? "url(#rs-welcome-ribbon-soft)"
                : "url(#rs-welcome-ribbon)"
            }
          />
        ))}

        <g
          fill="none"
          stroke="var(--rs-accent)"
          strokeLinecap="round"
          strokeWidth="2.5"
          opacity="0.25"
        >
          {scene.strokes.map((d, i) => (
            <path key={`stroke-${i}`} d={d} opacity={0.55 + i * 0.1} />
          ))}
        </g>

        <g fill="var(--rs-accent)">
          {scene.nodes.map((node, i) => (
            <circle
              key={`node-${i}`}
              cx={node.cx}
              cy={node.cy}
              r={node.r}
              opacity={node.opacity}
            />
          ))}
        </g>
      </svg>
    </div>
  );
}
